import { message } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
// import { useDispatch, useSelector } from 'react-redux'
import {
  addFinanceOrder,
  approveFinanceOrder,
  closeFinanceOrder,
  deleteFinanceOrder,
  deleteProformaOrder,
  editFinanceOrder,
  executeImportFinanceOrder,
  getFinanceOrder,
  getFinanceOrderPublicUrl,
  getFinanceOrders,
  getMinTransDateFinanceOrder,
  getProformaOrders,
  getRecurringOrders,
  getTotalFinanceOrders,
  getTotalOrderPerOutlet,
  inputMassCreateFinanceOrder,
  orderPaymentFinanceBankTrans,
  rejectFinanceOrder,
  revertApprovalFinanceOrder,
  uncloseFinanceOrder,
  uploadImportFinanceOrder,
} from 'utils/apis'
import { sendFinanceOrderSms } from '../apis'
// import { optionsSelector } from 'app-redux/selectors'
// import { editOption } from 'app-redux/options/actions'

export const useOrders = ({
  payload = {},
  enabled = false,
  // saveDataPagination = true,
  options = {},
  apiOptions = {},
}) => {
  // const optionsState = useSelector(optionsSelector)
  // const dispatch = useDispatch()

  // if (saveDataPagination) {
  //   // prevent user that already setting per_page > 100
  //   if (payload.per_page > 100) {
  //     payload.per_page = 100
  //   }

  //   if (payload.per_page && payload.per_page !== optionsState.sales_per_page_default) {
  //     dispatch(
  //       editOption({
  //         sales_per_page_default: payload.per_page,
  //       }),
  //     )
  //   }
  // }

  return useQuery(
    ['order', payload],
    async () => {
      const {
        data: { data },
      } = await getFinanceOrders(payload, apiOptions)
      return data
    },
    {
      enabled,
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export function useOrder({ id, payload = {}, enabled = false, apiOptions = {} }) {
  return useQuery(
    ['order', id, payload],
    async () => {
      let res
      try {
        res = await getFinanceOrder(id, payload, apiOptions)
      } catch (error) {
        // Interceptor axios me-reject dengan error.response, jadi status ada di error.status
        const err = new Error('Failed to load data from server!')
        err.status = error?.status ?? error?.response?.status
        throw err
      }
      // BE bisa balikan HTTP 200 dengan body { success: false } saat data tidak ada
      const body = res?.data
      if (body?.success === false) {
        const err = new Error(body?.message || 'Data tidak ditemukan')
        err.status = 404
        throw err
      }
      return body?.data
    },
    {
      enabled: !!id && enabled,
      // 404 (data tidak ditemukan) ditangani lewat tampilan khusus, jangan tampilkan toast
      onError: (error) => {
        if (error?.status !== 404) {
          message.error(error?.message || 'Failed to load data from server!')
        }
      },
    },
  )
}

export const useTotalOrders = (payload = {}, options = {}) => {
  return useQuery(
    ['total-orders', payload],
    async () => {
      const {
        data: { data },
      } = await getTotalFinanceOrders(payload)
      return data
    },
    {
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useRecurringOrders = ({ payload = {}, enabled = false }) => {
  return useQuery(
    ['order', 'recurring', payload],
    async () => {
      const {
        data: { data },
      } = await getRecurringOrders(payload)
      return data
    },
    {
      enabled,
      keepPreviousData: true,
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
    },
  )
}

export function useMutationOrderPaymentFinanceBankTrans({ options } = {}) {
  return useMutation((payload) => orderPaymentFinanceBankTrans(payload), options)
}

export function useUploadImportFinanceOrderMutation() {
  return useMutation(uploadImportFinanceOrder)
}

export function useExecuteImportFinanceOrderMutation() {
  return useMutation(executeImportFinanceOrder)
}

export const useGetMinTransDateFinanceOrderMutation = () => {
  return useMutation((payload) => getMinTransDateFinanceOrder({ ...payload, trans_type: 'order' }))
}

export const useInputMassCreateFinanceOrderMutation = () => {
  return useMutation(inputMassCreateFinanceOrder)
}

export const useProformaOrders = ({ payload, enabled }) => {
  return useQuery(
    ['proforma-orders', payload],
    async () => {
      const {
        data: { data },
      } = await getProformaOrders({ params: payload })
      return data
    },
    {
      enabled,
      onError: (error) => message.error(error?.data?.message || 'Failed to load data from server!'),
    },
  )
}

export const useDeleteProformaOrder = () => {
  return useMutation((payload) => deleteProformaOrder(payload.id))
}

export const useApproveOrder = () => {
  const queryClient = useQueryClient()
  return useMutation(approveFinanceOrder, {
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries(['myApprovableTotal'])
      }
    },
  })
}

export const useRejectOrder = () => {
  const queryClient = useQueryClient()
  return useMutation(rejectFinanceOrder, {
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries(['myApprovableTotal'])
      }
    },
  })
}

export const useRevertApprovalOrder = () => {
  const queryClient = useQueryClient()
  return useMutation(revertApprovalFinanceOrder, {
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries(['myApprovableTotal'])
      }
    },
  })
}

export const useDeleteOrder = () => {
  return useMutation((payload) => deleteFinanceOrder(payload.id))
}

export function useOrderPublicUrl(id, options = {}) {
  return useQuery(
    ['orderPublicUrl', id],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getFinanceOrderPublicUrl(id)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      enabled: !!id,
      onError: (error) => message.error(error?.message || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export const useCreateOrder = () => {
  return useMutation(({ apiOptions, ...data }) => addFinanceOrder(data, apiOptions))
}

export const useEditOrder = () => {
  return useMutation(({ apiOptions, ...data }) => editFinanceOrder(data, apiOptions))
}

export const useTotalOrderPerOutlet = (payload = {}, options = {}, apiOptions = {}) => {
  return useQuery(
    ['total-order-per-outlet', payload],
    async () => {
      const {
        data: { data },
      } = await getTotalOrderPerOutlet(payload, apiOptions)
      return data
    },
    {
      onError: (error) => {
        message.error(error?.message || 'Failed to load data from server!')
      },
      ...options,
    },
  )
}

export const useSendSMSOrder = () => {
  return useMutation(({ id, apiOptions, ...payload }) =>
    sendFinanceOrderSms(id, payload, apiOptions),
  )
}

export const useCloseFinanceOrderMutation = () => {
  return useMutation(({ apiOptions, ...data }) => closeFinanceOrder(data, apiOptions))
}

export const useUncloseFinanceOrderMutation = () => {
  return useMutation(uncloseFinanceOrder)
}
